import {
  from,
  split,
  type DefaultOptions,
  type HttpOptions,
  type InMemoryCacheConfig
} from '@apollo/client/core'
import { getMainDefinition } from '@apollo/client/utilities'
import { onError } from '@apollo/client/link/error'
import { setContext } from '@apollo/client/link/context'
import { createClient, type ClientOptions } from 'graphql-ws'
import { GraphQLWsLink } from '@apollo/client/link/subscriptions'
import createUploadLink from 'apollo-upload-client/createUploadLink.mjs'
import type { CookieOptions } from 'nuxt/app'
import { NuxtApollo, type ApolloClientKeys } from '#apollo'

type CookieAttributes = Omit<CookieOptions, 'encode' | 'decode' | 'expires' | 'default'>
type ClientConfig = {
  /**
   * The GraphQL endpoint.
   * @type {string}
   */
  httpEndpoint: string

  /**
   * Provide a GraphQL endpoint to be used client-side. Overrides `httpEndpoint`.
   * @type {string}
   **/
  browserHttpEndpoint?: string

  /**
   * Provide additional configuration for the `HttpLink`.
   * See https://www.apollographql.com/docs/link/links/http.html#options
   * @type {HttpOptions}
   **/
  httpLinkOptions?: Omit<HttpOptions, 'uri'>

  /**
   * Provide additional configuration for the `GraphQLWsLink`.
   * See https://github.com/enisdenjo/graphql-ws/blob/master/docs/interfaces/client.ClientOptions.md
   **/
  wsLinkOptions?: Omit<ClientOptions, 'url' | 'connectionParams'>

  /**
   * Specify a websocket endpoint to be used for subscriptions.
   * The `wss` protocol is recommended in production.
   * @type {string}
   **/
  wsEndpoint?: string

  // Enable Automatic Query persisting with Apollo Engine
  // persisting?: boolean

  /**
   * Specify if the client should solely use WebSocket.
   * requires `wsEndpoint`.
   * @type {boolean}
   * @default false
   **/
  websocketsOnly?: boolean

  /**
   * Specify if the client should be able to connect to the Apollo Client Devtools in production mode.
   * @type {boolean}
   * @default false
   **/
  connectToDevTools?: boolean

  /**
   * Configure default options to be applied to the apollo client.
   **/
  defaultOptions?: DefaultOptions

  /**
   * Configure the in-memory cache.
   **/
  inMemoryCacheOptions?: InMemoryCacheConfig

  /**
   * Specify the name under which the token will be stored.
   * as in either a cookie or localStorage.
   * @type {string}
   * @default "apollo:<client-name>.token"
   */
  tokenName?: string

  /**
   * Specify if the auth token should be stored in `cookie` or `localStorage`.
   * `Cookie` storage is required for SSR.
   * @type {string}
   * @default "cookie"
   **/
  tokenStorage?: 'cookie' | 'localStorage'

  /**
   * Specify the Authentication scheme.
   * @type {string}
   * @default "Bearer"
   **/
  authType?: string | null

  /**
   * Name of the Authentication token header.
   * @type {string}
   * @default "Authorization"
   */
  authHeader?: string

  /**
   * Configuration for the auth cookie.
   **/
  cookieAttributes?: CookieAttributes
}

export default defineNuxtPlugin(async nuxtApp => {
  const { $apollo } = useNuxtApp()
  const client = $apollo.defaultClient
  const requestCookies =
    (import.meta.server && NuxtApollo.proxyCookies && useRequestHeaders(['cookie'])) || undefined

  for (const [key, clientConfig] of Object.entries(NuxtApollo.clients) as [
    ApolloClientKeys,
    ClientConfig
  ][]) {
    const getAuth = async () => {
      const token = ref<string | null>(null)

      await nuxtApp.callHook('apollo:auth', { token, client: key })

      if (!token.value) {
        if (clientConfig.tokenStorage === 'cookie') {
          if (import.meta.client) {
            const t = useCookie(clientConfig.tokenName!).value
            if (t) token.value = t
          } else if (requestCookies?.cookie) {
            const t = requestCookies.cookie
              .split(';')
              .find(c => c.trim().startsWith(`${clientConfig.tokenName}=`))
              ?.split('=')?.[1]
            if (t) token.value = t
          }
        } else if (import.meta.client && clientConfig.tokenStorage === 'localStorage') {
          token.value = localStorage.getItem(clientConfig.tokenName!)
        }

        if (!token.value) return
      }

      const authScheme = !!token.value?.match(/^[a-zA-Z]+\s/)?.[0]

      if (authScheme || clientConfig?.authType === null) return token.value

      return `${clientConfig?.authType} ${token.value}`
    }

    const authLink = setContext(async (_, { headers }) => {
      const auth = await getAuth()

      if (!auth) return

      return {
        headers: {
          ...headers,
          ...(requestCookies && requestCookies),
          [clientConfig.authHeader!]: auth
        }
      }
    })

    const httpLink = createUploadLink({ uri: clientConfig.httpEndpoint })
    const wsLink =
      typeof window !== 'undefined'
        ? new GraphQLWsLink(
            createClient({
              url: clientConfig.wsEndpoint
            })
          )
        : null

    const splitLink =
      typeof window !== 'undefined' && wsLink != null
        ? split(
            ({ query }) => {
              const definition = getMainDefinition(query)
              return (
                definition.kind === 'OperationDefinition' && definition.operation === 'subscription'
              )
            },
            wsLink,
            httpLink
          )
        : httpLink

    const errorLink = onError(err => {
      nuxtApp.callHook('apollo:error', err)
    })

    client.setLink(from([errorLink, authLink, splitLink]))
  }
})
